import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { getAboutBlocks, createAboutBlock, updateAboutBlock, deleteAboutBlock, reorderAboutBlocks } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import AboutBlockForm from '../components/forms/AboutBlockForm';
import { SortableBlockRow } from '../components/SortableBlockRow';

const GestioneAbout = () => {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [blockToDelete, setBlockToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingBlock, setEditingBlock] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchBlocks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAboutBlocks();
            setBlocks(response.data);
        } catch (err) {
            setError("Impossibile caricare i blocchi della pagina Chi siamo.");
            toast.error("Errore nel caricamento dei blocchi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBlocks();
    }, [fetchBlocks]);

    const handleDeleteClick = (block) => {
        setBlockToDelete(block);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (blockToDelete) {
            try {
                await deleteAboutBlock(blockToDelete.id);
                toast.success("Blocco eliminato con successo!");
                fetchBlocks();
            } catch (err) {
                toast.error("Errore durante l'eliminazione del blocco.");
            } finally {
                setShowDeleteModal(false);
                setBlockToDelete(null);
            }
        }
    };

    const handleShowNewModal = () => {
        setEditingBlock(null);
        setShowFormModal(true);
    };

    const handleShowEditModal = (block) => {
        setEditingBlock(block);
        setShowFormModal(true);
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
        setEditingBlock(null);
    };

    const handleSave = async (formData) => {
        const isNew = !editingBlock;
        try {
            if (isNew) {
                await createAboutBlock(formData);
                toast.success("Blocco creato con successo!");
            } else {
                await updateAboutBlock(editingBlock.id, formData);
                toast.success("Blocco aggiornato con successo!");
            }
            fetchBlocks();
            handleCloseFormModal();
        } catch (err) {
            toast.error("Errore nel salvataggio del blocco.");
        }
    };

    async function handleDragEnd(event) {
        const { active, over } = event;
        if (!over) return;
        if (active.id !== over.id) {
            const oldIndex = blocks.findIndex((b) => b.id === active.id);
            const newIndex = blocks.findIndex((b) => b.id === over.id);
            const newOrderBlocks = arrayMove(blocks, oldIndex, newIndex);
            setBlocks(newOrderBlocks);
            const orderedIds = newOrderBlocks.map(b => b.id);
            try {
                await reorderAboutBlocks(orderedIds);
                toast.success("Ordine dei blocchi salvato!");
                fetchBlocks();
            } catch (err) {
                toast.error("Errore durante il salvataggio dell'ordine.");
                setBlocks(blocks);
            }
        }
    }

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}><Spinner animation="border" /></div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col><h2>Gestione blocchi "Chi siamo"</h2></Col>
                <Col className="text-end">
                    <Button onClick={handleShowNewModal} variant="primary">
                        <FontAwesomeIcon icon={faPlus} /> Nuovo Blocco
                    </Button>
                </Col>
            </Row>
            <Card>
                <Card.Body>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={blocks.map(b => b.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th style={{width: '50px'}}></th>
                                        <th>Tipo</th>
                                        <th>Contenuto</th>
                                        <th>Visibile</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {blocks.map(block => (
                                        <SortableBlockRow
                                            key={block.id}
                                            block={block}
                                            handleShowEditModal={handleShowEditModal}
                                            handleDeleteClick={handleDeleteClick}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        </SortableContext>
                    </DndContext>
                    {blocks.length === 0 && !loading && (
                        <div className="text-center p-3">Nessun blocco trovato. Inizia creandone uno nuovo.</div>
                    )}
                </Card.Body>
            </Card>

            <ConfirmModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleConfirm={handleConfirmDelete}
                title="Conferma Eliminazione"
                body={`Sei sicuro di voler eliminare il blocco?`}
            />

            <AboutBlockForm
                show={showFormModal}
                onHide={handleCloseFormModal}
                onSubmit={handleSave}
                initialData={editingBlock}
            />
        </Container>
    );
};

export default GestioneAbout;

